(() => {
  // The fact tiles of the refreshed mattress page (snippets/orion-mattress-facts.liquid) open as one set of scenes in the
  // gallery's full-screen viewer (orion-mattress-viewer-fast.js, openView({ scenes })), each growing out of its tile:
  // thickness on the side close-up with its measure, the warranty, the construction with a light passing down its
  // layers, the Roll Pack carton with its unpacking steps, the Coolmax fabric of a mattress that has a Coolmax side, and
  // firmness on its scale with the nearest softer and firmer ORION mattress. A tile with no scene (the gallery holds no
  // such picture) goes to the part of the page it names.

  // Where the side of the mattress stands in its angled photograph, as fractions of the photograph, measured on the
  // file: the measure runs up that side, from its bottom edge to its top edge. On other photographs the measure stands
  // as a caption, never on a guess.
  const MEASURES = {
    'orion_atlas_01_angled_20260924.png': { x: .865, top: .51, bottom: .657 }
  };
  // Each unpacking step plays for 5 s, as on the guide page (/pages/odhgies).
  const STEP_MS = 5000;
  const fileKey = source => {
    try { return decodeURIComponent(new URL(source, location.href).pathname.split('/').pop()).toLowerCase(); }
    catch (_) { return ''; }
  };
  const requests = new Map();
  const once = (url, read) => {
    if (!requests.has(url)) {
      requests.set(url, fetch(url, { credentials: 'same-origin' })
        .then(response => (response.ok ? response.text() : null))
        .then(text => (text ? read(text) : null))
        .catch(() => null));
    }
    return requests.get(url);
  };

  const viewIndex = (gallery, kinds) => {
    const usable = gallery.usableIndices?.() || [];
    for (const kind of kinds) {
      const index = gallery._views.findIndex(view => view.dataset.kind === kind);
      if (index >= 0 && usable.includes(index)) return index;
    }
    return -1;
  };

  // Thickness: the measure, drawn up the side of the mattress, with the thickness beside it.
  function measure(scene, value) {
    const plane = scene.querySelector('[data-image-plane]'), image = plane?.querySelector('img');
    if (!plane || !image) return;
    const spot = MEASURES[fileKey(image.getAttribute('src') || image.dataset.fullSrc || '')];
    const mark = document.createElement('div');
    mark.className = 'orion-fact-measure' + (spot ? '' : ' is-free');
    mark.setAttribute('aria-hidden', 'true');
    mark.innerHTML = '<span class="orion-fact-measure__line"></span><span class="orion-fact-measure__value"></span>';
    mark.lastChild.textContent = value;
    // A caption stands under the picture, outside the close-up's cut (the plane is masked to it).
    if (!spot) { (plane.parentElement || plane).appendChild(mark); return; }
    plane.appendChild(mark);
    // The viewer frames the close-up after it is made, and again on every resize: the measure follows the picture.
    const place = () => {
      if (!image.offsetWidth) return;
      mark.style.left = image.offsetLeft + spot.x * image.offsetWidth + 'px';
      mark.style.top = image.offsetTop + spot.top * image.offsetHeight + 'px';
      mark.style.height = (spot.bottom - spot.top) * image.offsetHeight + 'px';
    };
    new MutationObserver(place).observe(image, { attributes: true, attributeFilter: ['style'] });
    mark.place = place;
  }

  // Construction: each material keeps its place in the light that passes down the list (orion-mattress-refresh.css).
  function layers(scene) {
    scene.querySelectorAll('.orion-construction-labels__list > li').forEach((item, index) => item.style.setProperty('--i', index));
  }

  // The Roll Pack unpacking steps: the guide page's own steps (films and titles), from the copy of its content the page
  // carries, one after another under the carton. The film and the link under the steps both go to the guide page.
  function readGuide(template, titles) {
    if (!template) return null;
    const content = template.content;
    const slides = [...content.querySelectorAll('.rp-slide')].map(slide => {
      const film = slide.querySelector('video');
      return { title: slide.querySelector('h2')?.textContent.trim() || '', film: film?.getAttribute('src') || '', poster: film?.getAttribute('poster') || '' };
    }).filter(step => step.title && step.film);
    // A page in another language than the guide's names the steps itself, in order.
    titles?.forEach((title, index) => { if (slides[index] && title) slides[index].title = title; });
    return slides.length ? { slides, url: template.dataset.url || '' } : null;
  }
  function steps(scene, guide, labels) {
    const view = scene.querySelector('.orion-mattress-gallery__view');
    if (!guide || !view) return;
    const panel = document.createElement('div');
    panel.className = 'orion-fact-steps';
    panel.style.setProperty('--count', guide.slides.length);
    const frame = guide.url ? 'a' : 'div';
    panel.innerHTML = '<' + frame + ' class="orion-fact-steps__film"><video muted playsinline loop preload="none" disablepictureinpicture disableremoteplayback tabindex="-1"></video></' + frame + '>' +
      '<div class="orion-fact-steps__copy"><span class="orion-fact-steps__count"></span><strong class="orion-fact-steps__title"></strong><span class="orion-fact-steps__bars"></span></div>';
    const copy = panel.querySelector('.orion-fact-steps__copy'), video = panel.querySelector('video');
    const bars = guide.slides.map((step, index) => {
      const bar = document.createElement('button');
      bar.type = 'button';
      bar.setAttribute('aria-label', labels.step + ' ' + (index + 1) + ': ' + step.title);
      panel.querySelector('.orion-fact-steps__bars').appendChild(bar);
      return bar;
    });
    if (guide.url) {
      // The film is a second way to the same page, so it stays out of the tab order.
      const film = panel.querySelector('.orion-fact-steps__film');
      film.href = guide.url; film.tabIndex = -1; film.setAttribute('aria-hidden', 'true');
      const link = document.createElement('a');
      link.className = 'orion-fact-scene__action orion-fact-steps__more';
      link.href = guide.url;
      link.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6"/></svg>';
      link.prepend(labels.guide);
      copy.appendChild(link);
    }
    video.muted = true;
    video.addEventListener('loadedmetadata', () => { if (video.duration > 0) video.playbackRate = video.duration * 1000 / STEP_MS; });
    let timer = null;
    const show = index => {
      clearTimeout(timer);
      // The scene has gone (closed, or another scene shown): the film stops.
      if (!panel.isConnected) { video.pause(); return; }
      const step = guide.slides[index];
      panel.querySelector('.orion-fact-steps__count').textContent = labels.step + ' ' + (index + 1) + ' / ' + guide.slides.length;
      panel.querySelector('.orion-fact-steps__title').textContent = step.title;
      bars.forEach((bar, i) => {
        bar.classList.toggle('is-done', i < index);
        bar.classList.remove('is-on');
        if (i === index) bar.setAttribute('aria-current', 'step'); else bar.removeAttribute('aria-current');
      });
      void bars[index].offsetWidth;
      bars[index].classList.add('is-on');
      video.poster = step.poster;
      video.src = step.film;
      video.play().catch(() => {});
      timer = setTimeout(() => show((index + 1) % guide.slides.length), STEP_MS);
    };
    bars.forEach((bar, index) => bar.addEventListener('click', () => show(index)));
    panel.start = () => show(0);
    view.appendChild(panel);
  }

  // Firmness: the nearest softer and firmer mattress of the same series (templates/collection.firmness.liquid).
  const readList = text => { try { return JSON.parse(text); } catch (_) { return null; } };
  function neighbours(scene, source, handle, value) {
    const box = scene.querySelector('[data-neighbours]');
    if (!box || !source || !Number.isFinite(value)) return;
    const comma = !/^en\b/i.test(document.documentElement.lang);
    once(source, readList).then(list => {
      if (!Array.isArray(list) || !box.isConnected) return;
      const others = list.filter(item => item.h !== handle && Number.isFinite(item.f));
      const nearest = side => {
        const pool = others.filter(item => (side < 0 ? item.f < value : item.f > value));
        if (!pool.length) return null;
        const score = side < 0 ? Math.max(...pool.map(item => item.f)) : Math.min(...pool.map(item => item.f));
        return pool.find(item => item.f === score && item.a) || pool.find(item => item.f === score);
      };
      let shown = 0;
      for (const [side, card] of [[-1, box.querySelector('[data-side="softer"]')], [1, box.querySelector('[data-side="firmer"]')]]) {
        const item = card && nearest(side);
        if (!item) { card?.remove(); continue; }
        card.href = item.u;
        const picture = card.querySelector('img');
        if (item.i) picture.src = item.i; else picture.remove();
        card.querySelector('strong').textContent = item.t;
        const score = String(item.f);
        card.querySelector('small').textContent = [item.w, (comma ? score.replace('.', ',') : score) + '/10'].filter(Boolean).join(' · ');
        shown++;
      }
      box.hidden = !shown;
    });
  }

  function sceneSet(facts, gallery) {
    const labels = { step: facts.dataset.stepLabel || 'Step', guide: facts.dataset.guideLabel || 'Guide' };
    const set = [];
    for (const tile of facts.querySelectorAll('[data-orion-fact]')) {
      const kind = tile.dataset.orionFact;
      const entry = { kind, tile, label: tile.querySelector('.orion-fact__label')?.textContent.trim() || '', value: tile.dataset.value || '' };
      if (kind === 'thickness') {
        const view = viewIndex(gallery, ['detail', 'angled', 'front', 'image']);
        if (view < 0) continue;
        Object.assign(entry, { view, decorate: scene => measure(scene, entry.value), enter: scene => scene.querySelector('.orion-fact-measure')?.place?.() });
      } else if (kind === 'construction') {
        const view = viewIndex(gallery, ['cutaway', 'diagram']);
        if (view < 0) continue;
        Object.assign(entry, { view, decorate: layers });
      } else if (kind === 'packaging') {
        const view = viewIndex(gallery, ['package']);
        if (view < 0) continue;
        const guide = readGuide(facts.querySelector('template[data-orion-fact-guide]'), facts.dataset.guideSteps?.split('|'));
        Object.assign(entry, { view, decorate: scene => steps(scene, guide, labels), enter: scene => scene.querySelector('.orion-fact-steps')?.start?.() });
      } else {
        const template = facts.querySelector('template[data-orion-fact-scene="' + kind + '"]');
        if (!template) continue;
        entry.build = () => template.content.firstElementChild.cloneNode(true);
        if (kind === 'firmness') entry.enter = scene => neighbours(scene, facts.dataset.firmnessSource, facts.dataset.handle, Number(facts.dataset.firmness));
        if (kind === 'coolmax') entry.enter = scene => { const canvas = scene.querySelector('.orion-coolmax-flow'); if (canvas) airflow(canvas, { count: 760, alpha: .2, speed: 64, lineWidth: 1.1, avoid: 34 }).start(); };
      }
      set.push(entry);
    }
    return set;
  }

  // Opens the set of scenes at a tile's scene, growing out of `origin`: the tile, or whatever was pressed for it.
  function openFact(facts, tile, origin = tile) {
    const gallery = facts.closest('[data-orion-mattress-product]')?.querySelector('orion-mattress-gallery');
    const viewer = gallery?.querySelector('orion-image-viewer');
    const set = gallery?._shown && typeof viewer?.openView === 'function' ? sceneSet(facts, gallery) : [];
    const at = set.findIndex(entry => entry.tile === tile);
    if (at < 0) return false;
    if (origin !== tile) set[at] = { ...set[at], tile: origin };
    viewer.openView({ scenes: set, at });
    return true;
  }

  // Coolmax (owner, 2026-10-01): on a mattress with a Coolmax side, one mention of the fabric in its texts becomes a
  // button (the owner: one, not every one), the Cool Max symbol among its features and in their list opens the scene
  // too, and the photograph of that side (EN10, EN11, EN28) gets a current of air and a mark; all of these, like the mark
  // beside the series, open the Coolmax scene (its tile's), growing out of what was pressed.
  // The mention chosen is the fabric's own ("ύφασμα Coolmax" / "Coolmax fabric"), never the product's name.
  const COOL_MENTION = [/ύφασμα\s+(Cool\s?[Mm]ax|COOL\s?MAX|COOLMAX)/, /(Cool\s?[Mm]ax|COOL\s?MAX|COOLMAX)\s+fabric/];
  function markWord(product) {
    for (const scope of product.querySelectorAll('.orion-story__lead, .orion-story__more-inner')) {
      const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.parentElement?.closest('button, a')) continue;
        for (const pattern of COOL_MENTION) {
          const match = node.data.match(pattern);
          if (!match) continue;
          const word = node.splitText(match.index + match[0].indexOf(match[1]));
          word.splitText(match[1].length);
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'orion-term';
          button.dataset.orionCoolmaxOpen = '';
          button.textContent = match[1];
          word.replaceWith(button);
          return;
        }
      }
    }
  }

  function coolmax(facts) {
    const product = facts.closest('[data-orion-mattress-product]');
    if (!product) return;
    const tile = facts.querySelector('[data-orion-fact="coolmax"]');
    product.addEventListener('click', event => {
      const trigger = event.target.closest('[data-orion-coolmax-open]');
      if (!trigger || !tile) return;
      event.preventDefault();
      if (!openFact(facts, tile, trigger)) tile.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    // The list's row (not a button of its own) answers Enter and Space too.
    product.addEventListener('keydown', event => {
      const row = event.target.closest('[data-orion-coolmax-open][role="button"]');
      if (!row || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      row.click();
    });
    markWord(product);
    const symbol = facts.querySelector('template[data-orion-coolmax-symbol]');
    product.querySelectorAll('.orion-mattress-gallery__view[data-kind="alternate"]').forEach(view => coolPhoto(view, facts.dataset.coolmaxSide || 'Coolmax', symbol));
  }

  // The mouse as an obstacle to the air (computers only, owner 2026-10-01): where it is in a canvas's pixels, followed
  // closely, and how wide the room kept round it is (`reach` CSS pixels, grown in when the mouse comes over the canvas
  // and taken away when it leaves). It is placed on the middle of the arrow, not its tip, so nothing touches the arrow.
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  function pointerHole(canvas, reach) {
    if (!finePointer.matches) return null;
    const hole = { x: 0, y: 0, r: 0, gx: 0, gy: 0, want: 0, placed: false };
    const move = event => {
      if (event.pointerType !== 'mouse') return;
      const box = canvas.getBoundingClientRect();
      if (!box.width || !box.height) { hole.want = 0; return; }
      const scale = canvas.width / box.width, x = event.clientX + 4, y = event.clientY + 7;
      const over = x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;
      hole.gx = (x - box.left) * scale;
      hole.gy = (y - box.top) * scale;
      if (over && !hole.placed) { hole.x = hole.gx; hole.y = hole.gy; hole.placed = true; }
      hole.want = over ? reach * scale : 0;
    };
    const away = () => { hole.want = 0; };
    document.addEventListener('pointermove', move, { passive: true });
    document.documentElement.addEventListener('mouseleave', away);
    hole.step = dt => {
      const follow = 1 - Math.exp(-dt / .06);
      hole.x += (hole.gx - hole.x) * follow;
      hole.y += (hole.gy - hole.y) * follow;
      hole.r += (hole.want - hole.r) * (1 - Math.exp(-dt / .22));
      if (!hole.want && hole.r < .5) { hole.r = 0; hole.placed = false; }
    };
    hole.stop = () => {
      document.removeEventListener('pointermove', move);
      document.documentElement.removeEventListener('mouseleave', away);
    };
    return hole;
  }
  const smoothstep = (edge0, edge1, x) => { const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0))); return t * t * (3 - 2 * t); };

  // Air, drawn as fine streams of white light that follow a slow, smooth current: many short-lived points leave trails
  // that fade. `region(x)` keeps them to a band of the canvas (in canvas pixels), `mask` (a picture with transparency)
  // to the picture's subject, and `sheen` adds a soft band of light that crosses now and then. With `avoid` (CSS pixels)
  // the streams part round the mouse and close again behind it, as air flows round an obstacle, never touching it
  // (computers only). It stops by itself once the canvas has left the page.
  function airflow(canvas, { count, alpha, speed, lineWidth, mask = null, region = null, sheen = false, avoid = 0 }) {
    const ctx = canvas.getContext('2d');
    const trails = document.createElement('canvas'), tctx = trails.getContext('2d');
    const points = [];
    const hole = avoid ? pointerHole(canvas, avoid) : null;
    let width = 0, height = 0, ratio = 1, raf = 0, last = 0, clock = 0, running = false;
    const fit = () => {
      const box = canvas.getBoundingClientRect();
      ratio = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(box.width * ratio)), h = Math.max(1, Math.round(box.height * ratio));
      if (w !== width || h !== height) { width = canvas.width = trails.width = w; height = canvas.height = trails.height = h; }
    };
    // The current: mostly to the right, curling slowly.
    const heading = (x, y) => -.16 + .6 * Math.sin(x / width * 5.1 + clock * .32) * Math.cos(y / height * 3.7 - clock * .21) + .22 * Math.sin((x + 1.3 * y) / width * 8.4 + clock * .47);
    const seed = point => {
      point.x = Math.random() * width * 1.08 - width * .08;
      const band = region ? region(point.x) : null;
      point.y = band ? band[0] + Math.random() * (band[1] - band[0]) : Math.random() * height;
      point.life = 0;
      point.span = 1.4 + Math.random() * 3.4;
      point.speed = speed * (.55 + Math.random() * .9);
      return point;
    };
    const draw = now => {
      raf = 0;
      if (!running) return;
      if (!canvas.isConnected) { running = false; if (hole) hole.stop(); return; }
      const dt = Math.min(.05, last ? (now - last) / 1000 : .016);
      last = now; clock += dt;
      if (hole) hole.step(dt);
      tctx.globalCompositeOperation = 'destination-out';
      tctx.fillStyle = 'rgba(0,0,0,' + Math.min(.5, dt * 2.6).toFixed(3) + ')';
      tctx.fillRect(0, 0, width, height);
      tctx.globalCompositeOperation = 'lighter';
      tctx.lineCap = 'round';
      tctx.lineWidth = lineWidth * ratio;
      for (const point of points) {
        point.life += dt;
        const band = region ? region(point.x) : null;
        const outside = band ? point.y < band[0] || point.y > band[1] : point.y < -8 || point.y > height + 8;
        if (point.life > point.span || point.x > width * 1.04 || outside) seed(point);
        const turn = heading(point.x, point.y), step = point.speed * ratio * dt;
        let dx = Math.cos(turn) * step, dy = Math.sin(turn) * step;
        if (hole && hole.r > 1 && step > 0) {
          const R = hole.r, far = R * 3.2;
          let ox = point.x - hole.x, oy = point.y - hole.y, d = Math.hypot(ox, oy);
          if (d < far) {
            // A point the mouse came onto goes to the edge of the room at once (no line is drawn across it).
            if (d < R) {
              const k = R / (d || .001);
              ox = d ? ox * k : R; oy = d ? oy * k : 0; d = R;
              point.x = hole.x + ox; point.y = hole.y + oy;
            }
            // The flow round a round obstacle (zero across its edge, twice as fast at its sides), turned to the local
            // current and blended in from three times its size.
            const ux = dx / step, uy = dy / step, a = ox * ux + oy * uy, b = oy * ux - ox * uy, k = R * R / (d * d * d * d);
            const along = 1 - k * (a * a - b * b), across = -2 * k * a * b, w = smoothstep(far, far * .55, d);
            dx += ((along * ux - across * uy) * step - dx) * w;
            dy += ((along * uy + across * ux) * step - dy) * w;
          }
        }
        const x = point.x + dx, y = point.y + dy;
        // A point held back (before and behind the mouse) draws fainter, so no light piles up where the air meets.
        const pace = hole ? Math.min(1, Math.hypot(dx, dy) / (step || 1)) : 1;
        tctx.strokeStyle = 'rgba(255,255,255,' + (alpha * pace * Math.sin(Math.PI * point.life / point.span)).toFixed(3) + ')';
        tctx.beginPath(); tctx.moveTo(point.x, point.y); tctx.lineTo(x, y); tctx.stroke();
        point.x = x; point.y = y;
      }
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(trails, 0, 0);
      if (sheen) {
        const phase = (clock % 9) / 6;
        if (phase <= 1) {
          const x = (-.3 + phase * 1.6) * width;
          const light = ctx.createLinearGradient(x - width * .2, 0, x + width * .2, height * .3);
          light.addColorStop(0, 'rgba(255,255,255,0)'); light.addColorStop(.5, 'rgba(255,255,255,.16)'); light.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.fillStyle = light;
          ctx.fillRect(0, 0, width, height);
        }
      }
      if (mask) { ctx.globalCompositeOperation = 'destination-in'; ctx.drawImage(mask, 0, 0, width, height); ctx.globalCompositeOperation = 'source-over'; }
      raf = requestAnimationFrame(draw);
    };
    return {
      start() {
        if (running) return;
        fit();
        if (!points.length) for (let i = 0; i < count; i += 1) { const point = seed({}); point.life = Math.random() * point.span; points.push(point); }
        running = true; last = 0;
        raf = requestAnimationFrame(draw);
      },
      stop() { running = false; if (raf) cancelAnimationFrame(raf); raf = 0; },
      fit
    };
  }

  // The mouse's way over a canvas (computers only), in the canvas's pixels, gathered between frames: take() hands over
  // where it is and how far it went since it was last asked (null when it has not moved over the canvas).
  function pointerTrail(canvas) {
    if (!finePointer.matches) return null;
    let last = null, x = 0, y = 0, dx = 0, dy = 0;
    const move = event => {
      if (event.pointerType !== 'mouse') return;
      const box = canvas.getBoundingClientRect();
      if (!box.width || !box.height) { last = null; return; }
      const scale = canvas.width / box.width;
      const px = (event.clientX - box.left) * scale, py = (event.clientY - box.top) * scale;
      const over = px >= 0 && py >= 0 && px <= canvas.width && py <= canvas.height;
      if (over && last) { dx += px - last.x; dy += py - last.y; }
      last = over ? { x: px, y: py } : null;
      x = px; y = py;
    };
    document.addEventListener('pointermove', move, { passive: true });
    return {
      take() { if (!dx && !dy) return null; const way = { x, y, dx, dy }; dx = dy = 0; return way; },
      stop() { document.removeEventListener('pointermove', move); }
    };
  }

  // A cool mist that rolls slowly over the mattress's top: soft noise drawn at a low resolution and smoothed up, thickest
  // just over the knit and thinning above it, with a soft light crossing the knit now and then (kept to the mattress by
  // the picture's own alpha). `edge(x)` gives the top's upper edge for a fraction of the width, as a fraction of the
  // height. The canvas reaches LIFT of the picture's height above it, so the mist can rise over the mattress and fade
  // out before any edge. With `stir` the mouse moves the mist as a hand moves smoke (owner, 2026-10-01; computers only):
  // where it passes, the mist is carried along its way and curls a little to either side, then drifts back and settles.
  // It stops by itself once the canvas has left the page.
  const LIFT = .32;
  function coolMist(canvas, image, edge, stir = false) {
    const ctx = canvas.getContext('2d');
    const low = document.createElement('canvas'), lctx = low.getContext('2d');
    const band = document.createElement('canvas'), bctx = band.getContext('2d');
    const hand = stir ? pointerTrail(canvas) : null;
    let width = 0, height = 0, lw = 0, lh = 0, pixels = null, field = null, raf = 0, last = 0, clock = 0, running = false;
    const hash = (x, y) => { const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); };
    const ease = t => t * t * (3 - 2 * t);
    const noise = (x, y) => {
      const xi = Math.floor(x), yi = Math.floor(y), u = ease(x - xi), v = ease(y - yi);
      const a = hash(xi, yi), b = hash(xi + 1, yi), c = hash(xi, yi + 1), d = hash(xi + 1, yi + 1);
      return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
    };
    const fit = () => {
      const box = canvas.getBoundingClientRect(), ratio = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(box.width * ratio)), h = Math.max(1, Math.round(box.height * ratio));
      if (w === width && h === height) return;
      width = canvas.width = band.width = w; height = canvas.height = band.height = h;
      lw = 128; lh = Math.max(24, Math.round(lw * h / w));
      low.width = lw; low.height = lh;
      pixels = lctx.createImageData(lw, lh);
      field = hand ? new Float32Array(lw * lh * 2) : null;
    };
    // How far the mist of each cell of the low grid has been carried (x and y, in cells): it drifts back by itself, and
    // the hand's way since the last frame carries the mist near it along with it (most at its middle, a little curl to
    // either side of the way).
    const REACH = 11, FAR = 24;
    const carry = (way, dt) => {
      const keep = Math.exp(-dt / 1.4);
      for (let i = 0; i < field.length; i += 1) field[i] *= keep;
      if (!way) return;
      const cell = lw / width, mx = way.dx * cell, my = way.dy * cell, length = Math.hypot(mx, my);
      if (length < .05) return;
      const steps = Math.min(8, Math.ceil(length / 3)), ux = mx / length, uy = my / length;
      for (let step = 0; step < steps; step += 1) {
        const t = (step + .5) / steps, px = (way.x - way.dx * (1 - t)) * cell, py = (way.y - way.dy * (1 - t)) * cell;
        const ax = mx / steps * 1.1, ay = my / steps * 1.1;
        const x0 = Math.max(0, Math.floor(px - REACH)), x1 = Math.min(lw - 1, Math.ceil(px + REACH));
        const y0 = Math.max(0, Math.floor(py - REACH)), y1 = Math.min(lh - 1, Math.ceil(py + REACH));
        for (let y = y0; y <= y1; y += 1) {
          for (let x = x0; x <= x1; x += 1) {
            const ox = x - px, oy = y - py, d2 = ox * ox + oy * oy;
            if (d2 > REACH * REACH) continue;
            const w = Math.exp(-d2 / (REACH * REACH * .22)), side = (oy * ux - ox * uy) / REACH, k = (y * lw + x) * 2;
            field[k] += (ax - ay * side * .45) * w;
            field[k + 1] += (ay + ax * side * .45) * w;
            const m = Math.hypot(field[k], field[k + 1]);
            if (m > FAR) { field[k] *= FAR / m; field[k + 1] *= FAR / m; }
          }
        }
      }
    };
    const draw = now => {
      raf = 0;
      if (!running) return;
      if (!canvas.isConnected) { running = false; if (hand) hand.stop(); return; }
      const dt = Math.min(.05, last ? (now - last) / 1000 : .016);
      last = now; clock += dt;
      if (field) carry(hand.take(), dt);
      const data = pixels.data;
      for (let x = 0; x < lw; x += 1) {
        for (let y = 0; y < lh; y += 1) {
          const index = (y * lw + x) * 4;
          // Where this cell's mist came from (the hand may have carried it); the canvas's own edges stay where they are.
          const k = (y * lw + x) * 2, sx = field ? x - field[k] : x, sy = field ? y - field[k + 1] : y;
          const top = edge(Math.min(1, Math.max(0, sx / (lw - 1))));
          // Where the mist lies: thickest just over the far edge of the knit (seen against the dark), thinner over the
          // knit itself, fading out upwards and towards every edge of the canvas.
          const fy = sy / (lh - 1), depth = top === null ? -1 : fy * (1 + LIFT) - LIFT - top;
          const lie = depth < -.26 ? 0 : depth < 0 ? ease((depth + .26) / .26) : depth < .3 ? 1 - depth / .3 * .62 : Math.max(0, .38 - (depth - .3) * 3);
          const fx = x / (lw - 1), border = ease(Math.min(1, fx / .1, (1 - fx) / .1, y / (lh - 1) / .14));
          if (lie * border <= 0) { data[index + 3] = 0; continue; }
          const nx = sx / lw * 5.2 - clock * .3, ny = sy / lh * 4.2 + clock * .06;
          const cloud = .58 * noise(nx, ny) + .3 * noise(nx * 2.1 + 5.2, ny * 2.1) + .12 * noise(nx * 4.3, ny * 4.3 + 1.7);
          const thick = ease(Math.min(1, Math.max(0, (cloud - .44) / .34)));
          data[index] = data[index + 1] = data[index + 2] = 255;
          data[index + 3] = thick * lie * border * 92;
        }
      }
      lctx.putImageData(pixels, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(low, 0, 0, width, height);
      // The light crossing the knit, kept to the mattress: drawn under the mist, then cut to the picture's shape.
      const phase = (clock % 10) / 7;
      if (phase <= 1) {
        ctx.save();
        ctx.globalCompositeOperation = 'source-over';
        const x = (-.3 + phase * 1.6) * width;
        const light = ctx.createLinearGradient(x - width * .22, 0, x + width * .22, height * .3);
        light.addColorStop(0, 'rgba(255,255,255,0)'); light.addColorStop(.5, 'rgba(255,255,255,.14)'); light.addColorStop(1, 'rgba(255,255,255,0)');
        bctx.globalCompositeOperation = 'source-over';
        bctx.clearRect(0, 0, width, height);
        bctx.fillStyle = light;
        bctx.fillRect(0, 0, width, height);
        bctx.globalCompositeOperation = 'destination-in';
        bctx.drawImage(image, 0, height * LIFT / (1 + LIFT), width, height / (1 + LIFT));
        ctx.drawImage(band, 0, 0);
        ctx.restore();
      }
      raf = requestAnimationFrame(draw);
    };
    return {
      start() { if (running) return; fit(); running = true; last = 0; raf = requestAnimationFrame(draw); },
      stop() { running = false; if (raf) cancelAnimationFrame(raf); raf = 0; },
      fit
    };
  }

  // The photograph of the Coolmax side: a cool mist rolls slowly over the mattress's top (its transparent edge tells
  // where that is), moves with the mouse on a computer, and a soft light crosses the knit now and then; a mark with the
  // Coolmax symbol opens the scene. It draws only while the view is the one shown and on screen.
  function coolPhoto(view, label, symbol) {
    const plane = view.querySelector('[data-image-plane]'), image = plane?.querySelector('img');
    if (!plane || !image || view.querySelector('.orion-coolmax-mark')) return;
    const mark = document.createElement('button');
    mark.type = 'button';
    mark.className = 'orion-coolmax-mark';
    mark.dataset.orionCoolmaxOpen = '';
    const name = document.createElement('span');
    name.textContent = label;
    if (symbol) mark.append(symbol.content.cloneNode(true));
    mark.append(name);
    view.appendChild(mark);
    const canvas = document.createElement('canvas');
    canvas.className = 'orion-coolmax-air';
    canvas.setAttribute('aria-hidden', 'true');
    plane.appendChild(canvas);
    let edge = null, flow = null, onScreen = false;

    // The drawn picture inside the img box (object-fit: contain, at its object-position), measured where the picture
    // really is: the gallery centres it with a transform, which offsets do not include.
    const place = () => {
      const nw = image.naturalWidth, nh = image.naturalHeight, box = image.getBoundingClientRect(), frame = plane.getBoundingClientRect();
      if (!nw || !nh || !box.width || !box.height) return false;
      const scale = Math.min(box.width / nw, box.height / nh), dw = nw * scale, dh = nh * scale;
      const [px, py] = getComputedStyle(image).objectPosition.split(' ').map(v => (v.endsWith('%') ? parseFloat(v) / 100 : .5));
      Object.assign(canvas.style, { left: box.left - frame.left + (box.width - dw) * px + 'px', top: box.top - frame.top + (box.height - dh) * py - dh * LIFT + 'px', width: dw + 'px', height: dh * (1 + LIFT) + 'px' });
      return true;
    };
    // The mattress's upper edge, column by column, from the picture's alpha (null if the picture cannot be read).
    const readEdge = () => {
      try {
        const w = 240, h = Math.max(1, Math.round(w * image.naturalHeight / image.naturalWidth));
        const probe = document.createElement('canvas'); probe.width = w; probe.height = h;
        const pctx = probe.getContext('2d', { willReadFrequently: true });
        pctx.drawImage(image, 0, 0, w, h);
        const data = pctx.getImageData(0, 0, w, h).data, tops = new Array(w).fill(null);
        for (let x = 0; x < w; x += 1) for (let y = 0; y < h; y += 1) if (data[(y * w + x) * 4 + 3] > 170) { tops[x] = y / h; break; }
        return tops.some(v => v !== null) ? tops : null;
      } catch (_) { return null; }
    };
    const run = () => {
      const shown = onScreen && view.classList.contains('is-current');
      if (!shown) { if (flow) flow.stop(); return; }
      if (!place()) return;
      if (!flow) {
        edge = readEdge();
        const top = x => (edge ? edge[Math.min(edge.length - 1, Math.max(0, Math.round(x * (edge.length - 1))))] : .2);
        flow = coolMist(canvas, image, top, true);
      }
      flow.fit();
      flow.start();
    };
    if (!(image.complete && image.naturalWidth)) image.addEventListener('load', run);
    new ResizeObserver(() => { if (flow) { place(); flow.fit(); } }).observe(plane);
    new MutationObserver(run).observe(view, { attributes: true, attributeFilter: ['class'] });
    new IntersectionObserver(entries => { onScreen = entries[entries.length - 1].isIntersecting; run(); }, { threshold: .15 }).observe(view);
  }

  function init(facts) {
    if (facts.dataset.ready) return;
    facts.dataset.ready = 'true';
    facts.addEventListener('click', event => {
      const tile = event.target.closest('[data-orion-fact]');
      if (!tile || !facts.contains(tile)) return;
      if (openFact(facts, tile)) return;
      const target = tile.dataset.fallback && document.querySelector(tile.dataset.fallback);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    if (facts.dataset.coolmax) coolmax(facts);
  }
  const scan = scope => scope.querySelectorAll('[data-orion-facts]').forEach(init);
  scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
